import { useEffect, useMemo, useState } from "react";

import { useCreateMunicipalProperty } from "@/domain/municipal-property/hooks/api/use-create-municipal-property";
import { CreateMunicipalPropertyInput } from "@/graphql/generated/graphql";
import { extractGraphQLErrorCode } from "@/lib/extract-graphql-error-code";
import { useMutation, UseMutationResult } from "@tanstack/react-query";

interface CreateAndAddMunicipalPropertyResult {
  mutation: UseMutationResult<string, Error, CreateMunicipalPropertyInput>;
  hasCreatedProperty: boolean;
  hasAddError: boolean;
  hasUncertainCreate: boolean;
  reset: () => void;
}

export function useCreateAndAddMunicipalProperty({
  addProperty,
  scopeKey,
}: {
  addProperty: (propertyId: string) => Promise<string>;
  scopeKey: string;
}): CreateAndAddMunicipalPropertyResult {
  const create = useCreateMunicipalProperty();
  const [createdProperty, setCreatedProperty] = useState<{ scopeKey: string; id: string } | null>(null);
  const createdId = createdProperty?.scopeKey === scopeKey ? createdProperty.id : null;
  const scope = useMemo(() => ({ key: scopeKey, controller: new AbortController() }), [scopeKey]);
  useEffect(() => {
    scope.controller = new AbortController();
    return () => scope.controller.abort();
  }, [scope]);
  const mutation = useMutation({
    retry: false,
    mutationFn: async (input: CreateMunicipalPropertyInput): Promise<string> => {
      const { signal } = scope.controller;
      const propertyId = createdId ?? (await create.mutateAsync(input)).id;
      signal.throwIfAborted();
      setCreatedProperty({ scopeKey: scope.key, id: propertyId });
      const propertyYearId = await addProperty(propertyId);
      return propertyYearId;
    },
  });

  return {
    mutation,
    hasCreatedProperty: createdId !== null,
    hasAddError: createdId !== null && mutation.isError,
    hasUncertainCreate: createdId === null && mutation.isError && !extractGraphQLErrorCode(mutation.error),
    reset: () => {
      setCreatedProperty(null);
      mutation.reset();
    },
  };
}
